/**
 * Mock API Responses
 * Simulated backend responses for development
 */

import { User } from 'models/user.model';
import type {
  CategoryResult,
  CategoryTreeResult,
  CategoryFilter,
  CategoriesListResponse,
  CategoryImageResult,
  CategoryImageRequest,
  UpdateCategoryImageRequest
} from 'models/category.model';
import type { AddressResult } from 'models/address.model';
import type { ProductImageResult } from 'models/product.model';

/**
 * Mock user with password interface
 */
interface MockUser extends User {
  password: string;
}

/**
 * Mock product interface
 */
interface MockProduct {
  id: number;
  name: string;
  slug: string;
  price: number;
  category: number;
}

/**
 * Mock category interface
 */
interface MockCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  parentCategoryId?: string;
  displayOrder: number;
  isActive: boolean;
  image?: CategoryImageResult | null;
  createdAt: Date;
  updatedAt?: Date;
}

/**
 * API response interface
 */
interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * Simulate API delay
 */
const delay = (ms: number = 1000): Promise<void> => 
  new Promise((resolve) => setTimeout(resolve, ms));

// Mock users database
const mockUsers: MockUser[] = [
  {
    id: 1,
    email: 'admin@digitimax.com',
    password: 'Admin@123456',
    name: 'مدیر سیستم',
    role: 'admin',
    isActive: true,
    permissions: ['all']
  },
  {
    id: 2,
    email: 'admin@timax.com',
    password: 'admin123',
    name: 'Admin User',
    role: 'admin',
    isActive: true,
    permissions: ['all']
  },
  {
    id: 3,
    email: 'editor@timax.com',
    password: 'editor123',
    name: 'Editor User',
    role: 'editor',
    isActive: true,
    permissions: [
      'dashboard.view',
      'users.view',
      'products.view',
      'products.create',
      'products.edit',
      'categories.view',
      'categories.create',
      'categories.edit',
      'settings.view'
    ]
  },
  {
    id: 4,
    email: 'viewer@timax.com',
    password: 'viewer123',
    name: 'Viewer User',
    role: 'viewer',
    isActive: true,
    permissions: ['dashboard.view', 'users.view', 'products.view', 'categories.view']
  },
  {
    id: 5,
    email: 'user@timax.com',
    password: 'user123',
    name: 'Regular User',
    role: 'user',
    isActive: true,
    permissions: ['dashboard.view']
  }
];

// Mock products (updated to match ProductResult interface)
interface MockProductData {
  id: string;
  name: string;
  slug: string;
  description?: string;
  sku?: string;
  price: number;
  compareAtPrice?: number;
  stockQuantity: number;
  isActive: boolean;
  isPublished: boolean;
  publishedAt?: Date;
  createdAt: Date;
  updatedAt?: Date;
  images: Array<{
    id: string;
    imageBase64: string;
    contentType: string;
    fileName: string;
    fileSize: number;
    isPrimary: boolean;
    displayOrder: number;
  }>;
  categoryIds: string[];
  attributeValues: Record<string, string | number | boolean>;
}

function toMockProductImageResult(img: MockProductData['images'][number]): ProductImageResult {
  const path = `/api/media/${img.id}`;
  return {
    id: img.id,
    mediaId: img.id,
    url: path,
    thumbnailUrl: `${path}?w=100`,
    smallUrl: `${path}?w=300`,
    mediumUrl: `${path}?w=600`,
    largeUrl: `${path}?w=1200`,
    contentType: img.contentType,
    fileName: img.fileName,
    fileSize: img.fileSize,
    isPrimary: img.isPrimary,
    displayOrder: img.displayOrder,
    kind: 'Gallery'
  };
}

function toMockCategoryImageResult(
  image: CategoryImageRequest & { id?: string }
): CategoryImageResult {
  const mediaId = image.id ?? `cat-media-${Date.now()}`;
  const path = `/api/media/${mediaId}`;
  return {
    id: mediaId,
    mediaId,
    url: path,
    thumbnailUrl: `${path}?w=100`,
    smallUrl: `${path}?w=300`,
    mediumUrl: `${path}?w=600`,
    largeUrl: `${path}?w=1200`,
    contentType: image.contentType,
    fileName: image.fileName,
    altText: image.altText ?? null
  };
}

const mockProducts: MockProductData[] = [
  {
    id: '1',
    name: 'Samsung Galaxy S23 Ultra',
    slug: 'samsung-galaxy-s23-ultra',
    description: 'پرچمدار سامسونگ با قابلیت‌های پیشرفته',
    sku: 'SAM-S23U-256-BLK',
    price: 45000000,
    compareAtPrice: 50000000,
    stockQuantity: 15,
    isActive: true,
    isPublished: true,
    publishedAt: new Date('2024-01-15'),
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-20'),
    images: [
      {
        id: 'img-1',
        imageBase64: '/9j/4AAQSkZJRg...',
        contentType: 'image/jpeg',
        fileName: 's23-front.jpg',
        fileSize: 245678,
        isPrimary: true,
        displayOrder: 1
      }
    ],
    categoryIds: ['2'],
    attributeValues: {
      'attr-1': '256GB',
      'attr-2': 'Black',
      'attr-3': '12GB'
    }
  },
  {
    id: '2',
    name: 'MacBook Pro 14" M3',
    slug: 'macbook-pro-14-m3',
    description: 'لپ تاپ حرفه‌ای اپل با تراشه M3',
    sku: 'APL-MBP14-M3-512',
    price: 85000000,
    stockQuantity: 8,
    isActive: true,
    isPublished: true,
    publishedAt: new Date('2024-02-01'),
    createdAt: new Date('2024-01-25'),
    images: [
      {
        id: 'img-2',
        imageBase64: '/9j/4AAQSkZJRg...',
        contentType: 'image/jpeg',
        fileName: 'mbp-m3.jpg',
        fileSize: 198234,
        isPrimary: true,
        displayOrder: 1
      }
    ],
    categoryIds: ['3'],
    attributeValues: {
      'attr-4': '512GB SSD',
      'attr-5': 'Space Gray',
      'attr-6': '14 inch'
    }
  },
  {
    id: '3',
    name: 'iPhone 15 Pro Max',
    slug: 'iphone-15-pro-max',
    description: 'آیفون پرو مکس با تراشه A17 Pro',
    sku: 'APL-IP15PM-512-TIT',
    price: 65000000,
    compareAtPrice: 70000000,
    stockQuantity: 0,
    isActive: true,
    isPublished: false,
    createdAt: new Date('2024-02-10'),
    images: [],
    categoryIds: ['2'],
    attributeValues: {
      'attr-1': '512GB',
      'attr-2': 'Titanium',
      'attr-3': '8GB'
    }
  }
];

// Mock categories
const mockCategories: MockCategory[] = [
  {
    id: '1',
    name: 'الکترونیک',
    slug: 'electronics',
    description: 'محصولات الکترونیکی',
    displayOrder: 1,
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01')
  },
  {
    id: '2',
    name: 'موبایل',
    slug: 'mobile',
    description: 'گوشی های موبایل',
    parentCategoryId: '1',
    displayOrder: 1,
    isActive: true,
    createdAt: new Date('2024-01-02'),
    updatedAt: new Date('2024-01-02')
  },
  {
    id: '3',
    name: 'لپ تاپ',
    slug: 'laptop',
    description: 'کامپیوتر های قابل حمل',
    parentCategoryId: '1',
    displayOrder: 2,
    isActive: true,
    createdAt: new Date('2024-01-03'),
    updatedAt: new Date('2024-01-03')
  },
  {
    id: '4',
    name: 'لوازم خانگی',
    slug: 'home-appliances',
    description: 'لوازم برقی خانگی',
    displayOrder: 2,
    isActive: true,
    createdAt: new Date('2024-01-04'),
    updatedAt: new Date('2024-01-04')
  }
];

/**
 * Mock Auth API
 */
export const mockAuthAPI = {
  login: async (credentials: { email: string; password: string }): Promise<ApiResponse<{
    user: User;
    accessToken: string;
    refreshToken: string;
  }>> => {
    await delay(800);

    const user = mockUsers.find(
      (u) => u.email === credentials.email && u.password === credentials.password
    );

    if (!user) {
      throw new Error('Invalid credentials');
    }

    const { password, ...userWithoutPassword } = user;

    return {
      success: true,
      data: {
        user: userWithoutPassword,
        accessToken: `mock-access-token-${user.id}-${Date.now()}`,
        refreshToken: `mock-refresh-token-${user.id}-${Date.now()}`
      }
    };
  },

  register: async (userData: { email: string; password: string; name: string }): Promise<ApiResponse<{
    user: User;
    accessToken: string;
    refreshToken: string;
  }>> => {
    await delay(800);

    const existingUser = mockUsers.find((u) => u.email === userData.email);
    if (existingUser) {
      throw new Error('Email already exists');
    }

    const newUser: MockUser = {
      id: mockUsers.length + 1,
      ...userData,
      role: 'user',
      isActive: true,
      permissions: ['read']
    };

    mockUsers.push(newUser);

    const { password, ...userWithoutPassword } = newUser;

    return {
      success: true,
      data: {
        user: userWithoutPassword,
        accessToken: `mock-access-token-${newUser.id}-${Date.now()}`,
        refreshToken: `mock-refresh-token-${newUser.id}-${Date.now()}`
      }
    };
  },

  refresh: async (_refreshToken: string): Promise<ApiResponse<{ accessToken: string }>> => {
    await delay(500);

    return {
      success: true,
      data: {
        accessToken: `mock-access-token-refreshed-${Date.now()}`
      }
    };
  },

  logout: async (): Promise<ApiResponse> => {
    await delay(300);
    return { success: true, message: 'Logged out successfully' };
  }
};

/**
 * Mock Users API
 */
export const mockUsersAPI = {
  list: async (): Promise<ApiResponse<User[]>> => {
    await delay(600);
    return {
      success: true,
      data: mockUsers.map(({ password, ...user }) => user)
    };
  },

  getById: async (id: number | string): Promise<ApiResponse<User>> => {
    await delay(400);
    const user = mockUsers.find((u) => u.id === Number(id));
    if (!user) throw new Error('User not found');

    const { password, ...userWithoutPassword } = user;
    return { success: true, data: userWithoutPassword };
  },

  create: async (userData: Partial<MockUser>): Promise<ApiResponse<User>> => {
    await delay(600);
    const newUser: MockUser = {
      id: mockUsers.length + 1,
      email: userData.email || '',
      password: userData.password || '',
      name: userData.name || '',
      role: userData.role || 'user',
      isActive: true,
      permissions: userData.permissions || []
    };
    mockUsers.push(newUser);
    const { password, ...userWithoutPassword } = newUser;
    return { success: true, data: userWithoutPassword };
  },

  activate: async (id: number | string): Promise<ApiResponse> => {
    await delay(400);
    const user = mockUsers.find((u) => u.id === Number(id));
    if (!user) throw new Error('User not found');
    user.isActive = true;
    return { success: true, message: 'User activated' };
  },

  deactivate: async (id: number | string): Promise<ApiResponse> => {
    await delay(400);
    const user = mockUsers.find((u) => u.id === Number(id));
    if (!user) throw new Error('User not found');
    user.isActive = false;
    return { success: true, message: 'User deactivated' };
  },

  changePassword: async (id: number | string, passwords: { currentPassword: string; newPassword: string }): Promise<ApiResponse> => {
    await delay(500);
    const user = mockUsers.find((u) => u.id === Number(id) || String(u.id) === String(id));
    if (!user) throw new Error('User not found');
    if (user.password !== passwords.currentPassword) throw new Error('Current password is incorrect');
    user.password = passwords.newPassword;
    return { success: true, message: 'Password changed successfully' };
  }
};

/**
 * Mock Products API
 */
export const mockProductsAPI = {
  getAll: async (filter?: import('../../models/product.model').ProductFilter): Promise<import('../../models/product.model').ProductsListResponse> => {
    await delay(600);
    
    let filteredProducts = [...mockProducts];
    
    // Apply filters
    if (filter) {
      if (filter.searchTerm) {
        const term = filter.searchTerm.toLowerCase();
        filteredProducts = filteredProducts.filter(p => 
          p.name.toLowerCase().includes(term) || 
          p.description?.toLowerCase().includes(term) ||
          p.sku?.toLowerCase().includes(term)
        );
      }
      
      if (filter.categoryId) {
        filteredProducts = filteredProducts.filter(p => 
          p.categoryIds.includes(filter.categoryId!)
        );
      }
      
      if (filter.minPrice !== undefined) {
        filteredProducts = filteredProducts.filter(p => p.price >= filter.minPrice!);
      }
      
      if (filter.maxPrice !== undefined) {
        filteredProducts = filteredProducts.filter(p => p.price <= filter.maxPrice!);
      }
      
      // Sorting
      if (filter.sortBy) {
        filteredProducts.sort((a, b) => {
          let aVal = a[filter.sortBy as keyof MockProductData];
          let bVal = b[filter.sortBy as keyof MockProductData];
          
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
          }
          
          if (aVal < bVal) return filter.sortDescending ? 1 : -1;
          if (aVal > bVal) return filter.sortDescending ? -1 : 1;
          return 0;
        });
      }
    }
    
    const page = filter?.page || 1;
    const pageSize = filter?.pageSize || 10;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
    
    // Transform to ProductResult format
    const items = paginatedProducts.map(p => {
      const categories = p.categoryIds.map(catId => {
        const cat = mockCategories.find(c => c.id === catId);
        return {
          categoryId: catId,
          categoryName: cat?.name || 'Unknown'
        };
      });
      
      const isOnSale = !!p.compareAtPrice && p.compareAtPrice > p.price;
      const discountPercent = isOnSale 
        ? Math.round(((p.compareAtPrice! - p.price) / p.compareAtPrice!) * 100)
        : undefined;
      
      return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.description,
        sku: p.sku,
        price: p.price,
        compareAtPrice: p.compareAtPrice,
        stockQuantity: p.stockQuantity,
        isActive: p.isActive,
        isPublished: p.isPublished,
        publishedAt: p.publishedAt?.toISOString(),
        isOnSale,
        discountPercent,
        createdAt: p.createdAt.toISOString(),
        updatedAt: p.updatedAt?.toISOString(),
        images: p.images.map(toMockProductImageResult),
        categories,
        attributes: p.attributeValues
      };
    });
    
    const totalCount = filteredProducts.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    return {
      items,
      page,
      pageSize,
      totalCount,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1
    };
  },

  getById: async (id: string): Promise<import('../../models/product.model').ApiResponse<import('../../models/product.model').ProductResult>> => {
    await delay(400);
    const product = mockProducts.find((p) => p.id === id);
    if (!product) {
      return { success: false, error: 'Product not found' };
    }
    
    const categories = product.categoryIds.map(catId => {
      const cat = mockCategories.find(c => c.id === catId);
      return {
        categoryId: catId,
        categoryName: cat?.name || 'Unknown'
      };
    });
    
    const isOnSale = !!product.compareAtPrice && product.compareAtPrice > product.price;
    const discountPercent = isOnSale 
      ? Math.round(((product.compareAtPrice! - product.price) / product.compareAtPrice!) * 100)
      : undefined;
    
    return {
      success: true,
      data: {
        id: product.id,
        name: product.name,
        slug: product.slug,
        description: product.description,
        sku: product.sku,
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        stockQuantity: product.stockQuantity,
        isActive: product.isActive,
        isPublished: product.isPublished,
        publishedAt: product.publishedAt?.toISOString(),
        isOnSale,
        discountPercent,
        createdAt: product.createdAt.toISOString(),
        updatedAt: product.updatedAt?.toISOString(),
        images: product.images.map(toMockProductImageResult),
        categories,
        attributes: product.attributeValues
      }
    };
  },

  getBySlug: async (slug: string): Promise<import('../../models/product.model').ApiResponse<import('../../models/product.model').ProductResult>> => {
    await delay(400);
    const product = mockProducts.find((p) => p.slug === slug);
    if (!product) {
      return { success: false, error: 'Product not found' };
    }
    
    const categories = product.categoryIds.map(catId => {
      const cat = mockCategories.find(c => c.id === catId);
      return {
        categoryId: catId,
        categoryName: cat?.name || 'Unknown'
      };
    });
    
    const isOnSale = !!product.compareAtPrice && product.compareAtPrice > product.price;
    const discountPercent = isOnSale 
      ? Math.round(((product.compareAtPrice! - product.price) / product.compareAtPrice!) * 100)
      : undefined;
    
    return {
      success: true,
      data: {
        id: product.id,
        name: product.name,
        slug: product.slug,
        description: product.description,
        sku: product.sku,
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        stockQuantity: product.stockQuantity,
        isActive: product.isActive,
        isPublished: product.isPublished,
        publishedAt: product.publishedAt?.toISOString(),
        isOnSale,
        discountPercent,
        createdAt: product.createdAt.toISOString(),
        updatedAt: product.updatedAt?.toISOString(),
        images: product.images.map(toMockProductImageResult),
        categories,
        attributes: product.attributeValues
      }
    };
  },

  create: async (productData: import('../../models/product.model').CreateProductRequest): Promise<import('../../models/product.model').ApiResponse<import('../../models/product.model').ProductResult>> => {
    await delay(600);
    const newProduct: MockProductData = {
      id: String(Date.now()),
      name: productData.name,
      slug: productData.slug,
      description: productData.description,
      sku: productData.sku,
      price: productData.price,
      compareAtPrice: productData.compareAtPrice,
      stockQuantity: productData.stockQuantity,
      isActive: true,
      isPublished: false,
      createdAt: new Date(),
      images: productData.images.map((img, idx) => ({
        id: `img-${Date.now()}-${idx}`,
        ...img,
        fileSize: Math.round((img.imageBase64.length * 3) / 4)
      })),
      categoryIds: productData.categoryIds,
      attributeValues: productData.attributeValues
    };
    
    mockProducts.push(newProduct);
    
    const categories = newProduct.categoryIds.map(catId => {
      const cat = mockCategories.find(c => c.id === catId);
      return {
        categoryId: catId,
        categoryName: cat?.name || 'Unknown'
      };
    });
    
    return {
      success: true,
      data: {
        id: newProduct.id,
        name: newProduct.name,
        slug: newProduct.slug,
        description: newProduct.description,
        sku: newProduct.sku,
        price: newProduct.price,
        compareAtPrice: newProduct.compareAtPrice,
        stockQuantity: newProduct.stockQuantity,
        isActive: newProduct.isActive,
        isPublished: newProduct.isPublished,
        publishedAt: newProduct.publishedAt?.toISOString(),
        isOnSale: false,
        createdAt: newProduct.createdAt.toISOString(),
        images: newProduct.images.map(toMockProductImageResult),
        categories,
        attributes: newProduct.attributeValues
      }
    };
  },

  update: async (id: string, productData: import('../../models/product.model').UpdateProductRequest): Promise<import('../../models/product.model').ApiResponse<import('../../models/product.model').ProductResult>> => {
    await delay(600);
    const index = mockProducts.findIndex((p) => p.id === id);
    if (index === -1) {
      return { success: false, error: 'Product not found' };
    }
    
    mockProducts[index] = {
      ...mockProducts[index],
      name: productData.name,
      slug: productData.slug,
      description: productData.description,
      sku: productData.sku,
      price: productData.price,
      compareAtPrice: productData.compareAtPrice,
      stockQuantity: productData.stockQuantity,
      isActive: productData.isActive,
      isPublished: productData.isPublished,
      updatedAt: new Date(),
      images: productData.images.map((img) => {
        const existing = img.id ? mockProducts[index].images.find((e) => e.id === img.id) : undefined;
        const imageBase64 = img.imageBase64 || existing?.imageBase64 || '';
        return {
          id: img.id ?? `img-${Date.now()}-${Math.random()}`,
          imageBase64,
          contentType: img.contentType || existing?.contentType || 'image/jpeg',
          fileName: img.fileName || existing?.fileName || 'image',
          fileSize: imageBase64 ? Math.round((imageBase64.length * 3) / 4) : existing?.fileSize || 0,
          isPrimary: img.isPrimary,
          displayOrder: img.displayOrder
        };
      }),
      categoryIds: productData.categoryIds,
      attributeValues: productData.attributeValues
    };
    
    const product = mockProducts[index];
    const categories = product.categoryIds.map(catId => {
      const cat = mockCategories.find(c => c.id === catId);
      return {
        categoryId: catId,
        categoryName: cat?.name || 'Unknown'
      };
    });
    
    const isOnSale = !!product.compareAtPrice && product.compareAtPrice > product.price;
    const discountPercent = isOnSale 
      ? Math.round(((product.compareAtPrice! - product.price) / product.compareAtPrice!) * 100)
      : undefined;
    
    return {
      success: true,
      data: {
        id: product.id,
        name: product.name,
        slug: product.slug,
        description: product.description,
        sku: product.sku,
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        stockQuantity: product.stockQuantity,
        isActive: product.isActive,
        isPublished: product.isPublished,
        publishedAt: product.publishedAt?.toISOString(),
        isOnSale,
        discountPercent,
        createdAt: product.createdAt.toISOString(),
        updatedAt: product.updatedAt?.toISOString(),
        images: product.images.map(toMockProductImageResult),
        categories,
        attributes: product.attributeValues
      }
    };
  },

  delete: async (id: string): Promise<import('../../models/product.model').ApiResponse> => {
    await delay(500);
    const index = mockProducts.findIndex((p) => p.id === id);
    if (index === -1) {
      return { success: false, error: 'Product not found' };
    }
    mockProducts.splice(index, 1);
    return { success: true, message: 'محصول با موفقیت حذف شد' };
  }
};

/**
 * Mock Categories API
 */
const toCategoryResult = (cat: MockCategory): CategoryResult => ({
  ...cat,
  subCategories: mockCategories
    .filter((c) => c.parentCategoryId === cat.id)
    .map((c) => ({ ...c, subCategories: [] }))
});

export const mockCategoriesAPI = {
  list: async (filter?: CategoryFilter): Promise<CategoriesListResponse> => {
    await delay(500);

    const page = filter?.page ?? 1;
    const pageSize = filter?.pageSize ?? 20;
    let filtered = [...mockCategories];

    if (filter?.onlyRoot) {
      filtered = filtered.filter((cat) => !cat.parentCategoryId);
    }

    if (filter?.searchTerm) {
      const term = filter.searchTerm.toLowerCase();
      filtered = filtered.filter((cat) => cat.name.toLowerCase().includes(term));
    }

    const totalCount = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const startIndex = (page - 1) * pageSize;
    const items = filtered.slice(startIndex, startIndex + pageSize).map(toCategoryResult);

    return {
      items,
      page,
      pageSize,
      totalCount,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1
    };
  },

  tree: async (): Promise<CategoryTreeResult[]> => {
    await delay(500);

    const buildTree = (parentId?: string): CategoryTreeResult[] => {
      return mockCategories
        .filter((cat) => cat.parentCategoryId === parentId)
        .map((cat) => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          displayOrder: cat.displayOrder,
          children: buildTree(cat.id)
        }));
    };

    return buildTree();
  },

  getById: async (id: string): Promise<ApiResponse<CategoryResult>> => {
    await delay(400);
    const category = mockCategories.find((c) => c.id === id);
    if (!category) {
      return { success: false, error: 'Category not found' };
    }
    const subCategories = mockCategories.filter(c => c.parentCategoryId === id).map(c => ({ ...c, subCategories: [] }));
    return { success: true, data: { ...category, subCategories } };
  },

  create: async (categoryData: {
    name?: string;
    slug?: string;
    description?: string;
    parentCategoryId?: string;
    displayOrder?: number;
    image?: CategoryImageRequest | null;
  }): Promise<ApiResponse<CategoryResult>> => {
    await delay(600);
    const image = categoryData.image ? toMockCategoryImageResult(categoryData.image) : null;
    const newCategory: MockCategory = {
      id: String(Date.now()),
      name: categoryData.name || '',
      slug: categoryData.slug || '',
      description: categoryData.description,
      parentCategoryId: categoryData.parentCategoryId,
      displayOrder: categoryData.displayOrder || 0,
      isActive: true,
      image,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    mockCategories.push(newCategory);
    return { success: true, data: { ...newCategory, subCategories: [] } };
  },

  update: async (
    id: string,
    categoryData: {
      name?: string;
      slug?: string;
      description?: string;
      parentCategoryId?: string;
      displayOrder?: number;
      isActive?: boolean;
      image?: CategoryImageRequest | null;
    }
  ): Promise<ApiResponse<CategoryResult>> => {
    await delay(600);
    const index = mockCategories.findIndex((c) => c.id === id);
    if (index === -1) {
      return { success: false, error: 'Category not found' };
    }

    const { image, ...rest } = categoryData;
    const next: MockCategory = {
      ...mockCategories[index],
      ...rest,
      updatedAt: new Date()
    };

    if (image === null) {
      next.image = null;
    } else if (image) {
      next.image = toMockCategoryImageResult(image);
    }

    mockCategories[index] = next;
    const subCategories = mockCategories.filter(c => c.parentCategoryId === id).map(c => ({ ...c, subCategories: [] }));
    return { success: true, data: { ...mockCategories[index], subCategories } };
  },

  delete: async (id: string): Promise<ApiResponse> => {
    await delay(500);
    const index = mockCategories.findIndex((c) => c.id === id);
    if (index === -1) {
      return { success: false, error: 'Category not found' };
    }
    // Check if category has children
    const hasChildren = mockCategories.some(c => c.parentCategoryId === id);
    if (hasChildren) {
      return { success: false, error: 'Cannot delete category with subcategories' };
    }
    mockCategories.splice(index, 1);
    return { success: true, message: 'دسته بندی با موفقیت حذف شد' };
  },

  uploadImage: async (id: string, imageData: UpdateCategoryImageRequest): Promise<ApiResponse<CategoryResult>> => {
    await delay(500);
    const index = mockCategories.findIndex((c) => c.id === id);
    if (index === -1) {
      return { success: false, error: 'Category not found' };
    }

    mockCategories[index] = {
      ...mockCategories[index],
      image: toMockCategoryImageResult(imageData),
      updatedAt: new Date()
    };

    const subCategories = mockCategories
      .filter((c) => c.parentCategoryId === id)
      .map((c) => ({ ...c, subCategories: [] }));

    return { success: true, data: { ...mockCategories[index], subCategories } };
  },

  removeImage: async (id: string): Promise<ApiResponse<CategoryResult>> => {
    await delay(500);
    const index = mockCategories.findIndex((c) => c.id === id);
    if (index === -1) {
      return { success: false, error: 'Category not found' };
    }

    mockCategories[index] = {
      ...mockCategories[index],
      image: null,
      updatedAt: new Date()
    };

    const subCategories = mockCategories
      .filter((c) => c.parentCategoryId === id)
      .map((c) => ({ ...c, subCategories: [] }));

    return { success: true, data: { ...mockCategories[index], subCategories } };
  }
};

// ==============================|| MOCK ATTRIBUTES ||============================== //

import { AttributeType, AttributeResult, CreateAttributeRequest, UpdateAttributeRequest } from 'models/attribute.model';

const mockAttributes: AttributeResult[] = [
  {
    id: '1',
    name: 'رنگ',
    slug: 'color',
    displayName: 'رنگ',
    type: AttributeType.MultiSelect,
    categoryId: '1',
    isRequired: true,
    isFilterable: true,
    displayOrder: 1,
    unit: null,
    options: [
      { id: '1-1', value: 'مشکی', displayValue: 'رنگ مشکی' },
      { id: '1-2', value: 'سفید', displayValue: 'رنگ سفید' },
      { id: '1-3', value: 'آبی', displayValue: 'رنگ آبی' }
    ]
  },
  {
    id: '2',
    name: 'حافظه داخلی',
    slug: 'storage',
    displayName: 'حافظه داخلی',
    type: AttributeType.MultiSelect,
    categoryId: '2',
    isRequired: true,
    isFilterable: true,
    displayOrder: 1,
    unit: 'GB',
    options: [
      { id: '2-1', value: '64', displayValue: '64 گیگابایت' },
      { id: '2-2', value: '128', displayValue: '128 گیگابایت' },
      { id: '2-3', value: '256', displayValue: '256 گیگابایت' }
    ]
  },
  {
    id: '3',
    name: 'سایز صفحه',
    slug: 'screen-size',
    displayName: 'سایز صفحه',
    type: AttributeType.Number,
    categoryId: '3',
    isRequired: false,
    isFilterable: true,
    displayOrder: 2,
    unit: 'اینچ',
    options: []
  },
  {
    id: '4',
    name: 'گارانتی',
    slug: 'warranty',
    displayName: 'گارانتی',
    type: AttributeType.Boolean,
    categoryId: '1',
    isRequired: false,
    isFilterable: true,
    displayOrder: 5,
    unit: null,
    options: []
  },
  {
    id: '5',
    name: 'برند',
    slug: 'brand',
    displayName: 'برند',
    type: AttributeType.Text,
    categoryId: '4',
    isRequired: true,
    isFilterable: false,
    displayOrder: 1,
    unit: null,
    options: []
  }
];

export const mockAttributesAPI = {
  getByCategory: async (categoryId: string, onlyFilterable?: boolean): Promise<ApiResponse<AttributeResult[]>> => {
    await delay(400);
    let filtered = mockAttributes.filter((attr) => attr.categoryId === categoryId);

    if (onlyFilterable) {
      filtered = filtered.filter((attr) => attr.isFilterable);
    }

    return { success: true, data: filtered };
  },

  create: async (request: CreateAttributeRequest): Promise<ApiResponse<AttributeResult>> => {
    await delay(600);
    const newAttribute: AttributeResult = {
      id: String(Date.now()),
      name: request.name,
      slug: request.slug,
      displayName: request.name,
      type: request.type,
      categoryId: request.categoryId,
      isRequired: request.isRequired,
      isFilterable: request.isFilterable,
      displayOrder: request.displayOrder,
      unit: request.unit ?? null,
      options:
        request.options?.map((opt, index) => ({
          id: `${Date.now()}-${index}`,
          value: opt,
          displayValue: opt
        })) ?? []
    };
    mockAttributes.push(newAttribute);
    return { success: true, data: newAttribute };
  },

  update: async (id: string, request: UpdateAttributeRequest): Promise<ApiResponse<AttributeResult>> => {
    await delay(600);
    const index = mockAttributes.findIndex((attr) => attr.id === id);
    if (index === -1) {
      return { success: false, error: 'Attribute not found' };
    }

    const updatedOptions =
      request.options?.map((opt) => ({
        id: opt.id ?? `${Date.now()}-${Math.random()}`,
        value: opt.value,
        displayValue: opt.displayValue
      })) ?? mockAttributes[index].options;

    mockAttributes[index] = {
      ...mockAttributes[index],
      name: request.name,
      slug: request.slug,
      displayName: request.name,
      type: request.type,
      isRequired: request.isRequired,
      isFilterable: request.isFilterable,
      displayOrder: request.displayOrder,
      unit: request.unit ?? null,
      options: updatedOptions
    };

    return { success: true, data: mockAttributes[index] };
  },

  delete: async (id: string): Promise<ApiResponse> => {
    await delay(500);
    const index = mockAttributes.findIndex((attr) => attr.id === id);
    if (index === -1) {
      return { success: false, error: 'Attribute not found' };
    }
    mockAttributes.splice(index, 1);
    return { success: true, message: 'ویژگی با موفقیت حذف شد' };
  }
};

// ==============================|| MOCK ADDRESSES ||============================== //

const mockAddresses: AddressResult[] = [
  {
    id: 'a1111111-1111-1111-1111-111111111111',
    userId: 'u1111111-1111-1111-1111-111111111111',
    title: 'خانه',
    province: 'تهران',
    city: 'تهران',
    addressLine: 'خیابان ولیعصر، پلاک ۱۲',
    unit: '۴',
    plaque: '۱۲',
    postalCode: '1234567890',
    fullName: 'علی محمدی',
    phoneNumber: '09121234567',
    isDefault: true,
    isActive: true,
    fullAddress: 'تهران، تهران، خیابان ولیعصر، پلاک ۱۲، واحد ۴',
    createdAt: new Date().toISOString()
  }
];

export const mockAddressesAPI = {
  getById: async (id: string): Promise<ApiResponse<AddressResult>> => {
    await delay(400);
    const address = mockAddresses.find((a) => a.id === id);
    if (!address) {
      return { success: false, error: 'Address not found' };
    }
    return { success: true, data: address };
  },

  getByUserId: async (userId: string): Promise<ApiResponse<AddressResult[]>> => {
    await delay(400);
    const addresses = mockAddresses.filter((a) => a.userId === userId);
    return { success: true, data: addresses };
  }
};
